import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { eventsApi, eventCommentsApi, eventAttendancesApi, eventMediasApi, teamsApi, gamesApi, getStaticUrl } from '../../api/client';
import { useAuth } from '../../contexts/AuthContext';
import { CalendarIcon, TrashIcon, TrophyIcon, SwordsIcon, DumbbellIcon, GraduationIcon, ClipboardIcon, InfoIcon, UsersCheckIcon, MessageSquareIcon, FileIcon, ImageIcon, VideoIcon, Edit2Icon, SaveIcon, CancelIcon, DownloadIcon, CloseIcon } from '../Icons/Icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import './EventDetail.css';
import { createSafeMarkup } from '../../utils/sanitize';

const EventDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, canManageTeam, hasPermission } = useAuth();
  const [event, setEvent] = useState(null);
  const [comments, setComments] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [medias, setMedias] = useState([]);
  const [teams, setTeams] = useState([]);
  const [games, setGames] = useState([]);
  const [loading, setLoading] = useState(true);

  // États pour les formulaires
  const [newComment, setNewComment] = useState('');
  const [replyingTo, setReplyingTo] = useState(null);
  const [myAttendance, setMyAttendance] = useState(null);
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // details, comments, attendances, medias
  const [isEditingNotes, setIsEditingNotes] = useState(false);
  const [richNotesContent, setRichNotesContent] = useState('');
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isEditingScore, setIsEditingScore] = useState(false);
  const [scoreData, setScoreData] = useState({ opponent: '', scoreTeam: '', scoreOpponent: '' });
  const [isEditingInfo, setIsEditingInfo] = useState(false);
  const [infoData, setInfoData] = useState({ location: '', is_online: false });

  useEffect(() => {
    loadEventData();
  }, [id]);

  const loadEventData = async () => {
    try {
      setLoading(true);
      const [eventRes, commentsRes, attendancesRes, mediasRes, teamsRes, gamesRes] = await Promise.all([
        eventsApi.getById(id),
        eventCommentsApi.getAll(id),
        eventAttendancesApi.getAll(id),
        eventMediasApi.getAll(id),
        teamsApi.getAll(),
        gamesApi.getAll()
      ]);

      setEvent(eventRes.data);
      setComments(commentsRes.data);
      setAttendances(attendancesRes.data);
      setMedias(mediasRes.data);
      setTeams(teamsRes.data);
      setGames(gamesRes.data);

      // Trouver ma présence/absence
      const myAtt = attendancesRes.data.find(a => a.user_id === user.id);
      setMyAttendance(myAtt);
    } catch (error) {
      console.error('Erreur chargement événement:', error);
      alert('Événement introuvable');
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const handleAddComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;

    try {
      await eventCommentsApi.create(id, newComment, replyingTo?.id || null);
      setNewComment('');
      setReplyingTo(null);
      await loadEventData();
    } catch (error) {
      console.error('Erreur ajout commentaire:', error);
      alert('Erreur lors de l\'ajout du commentaire');
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Supprimer ce commentaire ?')) return;

    try {
      await eventCommentsApi.delete(commentId);
      await loadEventData();
    } catch (error) {
      console.error('Erreur suppression commentaire:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSetAttendance = async (status) => {
    try {
      await eventAttendancesApi.set(id, status);
      await loadEventData();
    } catch (error) {
      console.error('Erreur mise à jour présence:', error);
      alert('Erreur lors de la mise à jour');
    }
  };

  const handleRemoveAttendance = async () => {
    try {
      await eventAttendancesApi.remove(id);
      await loadEventData();
    } catch (error) {
      console.error('Erreur suppression présence:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleMediaUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validation taille (500 MB max)
    if (file.size > 500 * 1024 * 1024) {
      alert('Fichier trop volumineux (max 500 MB)');
      return;
    }

    // Détection automatique du type de fichier
    let mediaType = 'file';
    if (file.type.startsWith('image/')) {
      mediaType = 'image';
    } else if (file.type.startsWith('video/')) {
      mediaType = 'video';
    }

    try {
      setUploadingMedia(true);
      await eventMediasApi.upload(id, file, mediaType);
      await loadEventData();
    } catch (error) {
      console.error('Erreur upload média:', error);
      alert('Erreur lors de l\'upload');
    } finally {
      setUploadingMedia(false);
    }
  };

  const handleDeleteMedia = async (mediaId) => {
    if (!window.confirm('Supprimer ce média ?')) return;

    try {
      await eventMediasApi.delete(mediaId);
      await loadEventData();
    } catch (error) {
      console.error('Erreur suppression média:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handleSaveRichNotes = async () => {
    try {
      await eventsApi.update(id, { rich_notes: richNotesContent });
      setIsEditingNotes(false);
      await loadEventData();
    } catch (error) {
      console.error('Erreur sauvegarde notes:', error);
      alert('Erreur lors de la sauvegarde des notes');
    }
  };

  const handleStartEditingNotes = () => {
    setRichNotesContent(event.rich_notes || '');
    setIsEditingNotes(true);
  };

  const parseScore = (score) => {
    if (!score) return { team: '', opponent: '' };
    const parts = score.split('-').map(s => s.trim());
    return { team: parts[0] || '', opponent: parts[1] || '' };
  };

  const handleStartEditingScore = () => {
    const parsed = parseScore(event.score);
    setScoreData({
      opponent: event.opponent || '',
      scoreTeam: parsed.team,
      scoreOpponent: parsed.opponent
    });
    setIsEditingScore(true);
  };

  const handleSaveScore = async () => {
    try {
      const score = (scoreData.scoreTeam || scoreData.scoreOpponent)
        ? `${scoreData.scoreTeam || '0'} - ${scoreData.scoreOpponent || '0'}`
        : '';
      await eventsApi.update(id, {
        opponent: scoreData.opponent,
        score: score || null
      });
      setIsEditingScore(false);
      await loadEventData();
    } catch (error) {
      console.error('Erreur sauvegarde score:', error);
      alert('Erreur lors de la sauvegarde du score');
    }
  };

  const handleStartEditingInfo = () => {
    setInfoData({
      location: event.location || '',
      is_online: event.is_online || false
    });
    setIsEditingInfo(true);
  };

  const handleSaveInfo = async () => {
    try {
      await eventsApi.update(id, {
        location: infoData.location,
        is_online: infoData.is_online
      });
      setIsEditingInfo(false);
      await loadEventData();
    } catch (error) {
      console.error('Erreur sauvegarde informations:', error);
      alert('Erreur lors de la sauvegarde des informations');
    }
  };

  const handleDeleteEvent = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet événement ? Cette action est irréversible.')) {
      return;
    }

    try {
      await eventsApi.delete(id);
      alert('Événement supprimé avec succès');
      navigate('/events');
    } catch (error) {
      console.error('Erreur suppression événement:', error);
      const errorMessage = error.response?.data?.detail || 'Erreur lors de la suppression de l\'événement';
      alert(errorMessage);
    }
  };

  const getEventTypeLabel = (type) => {
    const labels = {
      tournament: 'Tournoi',
      scrim: 'Scrim',
      training: 'Entraînement',
      coaching: 'Coaching',
      meeting: 'Réunion'
    };
    return labels[type] || type;
  };

  const getEventTypeIcon = (type, size = 14) => {
    const icons = {
      tournament: <TrophyIcon size={size} />,
      scrim: <SwordsIcon size={size} />,
      training: <DumbbellIcon size={size} />,
      coaching: <GraduationIcon size={size} />,
      meeting: <ClipboardIcon size={size} />
    };
    return icons[type] || <CalendarIcon size={size} />;
  };

  const getTeamNames = () => {
    if (!event) return 'Tous les jeux';

    // Gérer ancien format (team_id) et nouveau (team_ids)
    const teamIds = event.team_ids && event.team_ids.length > 0
      ? event.team_ids
      : (event.team_id ? [event.team_id] : []);

    if (teamIds.length === 0) {
      return 'Tous les jeux';
    }

    return teamIds
      .map(id => teams.find(t => t.id === id)?.name)
      .filter(Boolean)
      .join(', ') || 'Équipe inconnue';
  };

  const getGameName = () => {
    if (!event) return null;

    // Gérer ancien format (team_id) et nouveau (team_ids)
    const teamIds = event.team_ids && event.team_ids.length > 0
      ? event.team_ids
      : (event.team_id ? [event.team_id] : []);

    if (teamIds.length === 0) return null;

    const firstTeam = teams.find(t => t.id === teamIds[0]);
    if (!firstTeam) return null;

    const game = games.find(g => g.id === firstTeam.game_id);
    return game ? game.name : null;
  };

  const formatDateTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) return 'À l\'instant';
    if (diffMins < 60) return `Il y a ${diffMins}m`;
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    if (diffDays < 7) return `Il y a ${diffDays}j`;
    return formatDateTime(dateString);
  };

  const getAttendanceStats = () => {
    const present = attendances.filter(a => a.status === 'present').length;
    const absent = attendances.filter(a => a.status === 'absent').length;
    const maybe = attendances.filter(a => a.status === 'maybe').length;
    return { present, absent, maybe, total: attendances.length };
  };

  const canDeleteEvent = () => {
    if (!user) return false;
    return hasPermission('events.delete_all') || hasPermission('events.delete_own_team');
  };

  if (loading) {
    return <div className="event-detail-page loading">Chargement...</div>;
  }

  if (!event) {
    return <div className="event-detail-page">Événement introuvable</div>;
  }

  const stats = getAttendanceStats();

  return (
    <div className="event-detail-page">
      {/* Header */}
      <div className="event-detail-header">
        <button className="btn-back" onClick={() => navigate('/events')}>
          ← Retour
        </button>
        <div className="event-detail-title-section">
          <div className="event-type-badge" data-type={event.type}>
            {getEventTypeIcon(event.type)} {getEventTypeLabel(event.type)}
          </div>
          <h1>{event.title}</h1>
          <div className="event-meta-info">
            <span><CalendarIcon size={16} /> {formatDateTime(event.start_datetime)}</span>
            <span>→</span>
            <span>{formatDateTime(event.end_datetime)}</span>
          </div>
          <div className="event-teams-info">
            {getGameName() ? `${getGameName()} - ${getTeamNames()}` : getTeamNames()}
          </div>
        </div>
        {canDeleteEvent() && (
          <button className="btn-delete-event" onClick={handleDeleteEvent} title="Supprimer l'événement">
            <TrashIcon size={18} /> Supprimer
          </button>
        )}
      </div>

      {/* Tabs */}
      <div className="event-tabs">
        <button
          className={`event-tab ${activeTab === 'details' ? 'active' : ''}`}
          onClick={() => setActiveTab('details')}
        >
          <InfoIcon size={18} /> Détails
        </button>
        <button
          className={`event-tab ${activeTab === 'attendances' ? 'active' : ''}`}
          onClick={() => setActiveTab('attendances')}
        >
          <UsersCheckIcon size={18} /> Présences ({stats.total})
        </button>
        <button
          className={`event-tab ${activeTab === 'comments' ? 'active' : ''}`}
          onClick={() => setActiveTab('comments')}
        >
          <MessageSquareIcon size={18} /> Commentaires ({comments.length})
        </button>
        <button
          className={`event-tab ${activeTab === 'medias' ? 'active' : ''}`}
          onClick={() => setActiveTab('medias')}
        >
          <FileIcon size={18} /> Pièces jointes ({medias.length})
        </button>
      </div>

      {/* Content */}
      <div className="event-content">
        {/* ONGLET DÉTAILS */}
        {activeTab === 'details' && (
          <div className="event-details-tab">
            <div className="event-info-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Informations</h3>
                {!isEditingInfo && (
                  <button
                    onClick={handleStartEditingInfo}
                    className="btn-primary btn-sm"
                  >
                    <Edit2Icon size={14} /> Modifier
                  </button>
                )}
              </div>
              {isEditingInfo ? (
                <div>
                  <div className="info-edit-form">
                    <div className="input-group">
                      <label className="input-label">Lieu</label>
                      <input
                        type="text"
                        className="input"
                        value={infoData.location}
                        onChange={(e) => setInfoData({ ...infoData, location: e.target.value })}
                        placeholder="Adresse ou lieu de l'événement"
                      />
                    </div>
                    <div className="input-group">
                      <label className="input-label">Format</label>
                      <select
                        className="input"
                        value={infoData.is_online ? 'online' : 'offline'}
                        onChange={(e) => setInfoData({ ...infoData, is_online: e.target.value === 'online' })}
                      >
                        <option value="offline">📍 Présentiel</option>
                        <option value="online">🌐 En ligne</option>
                      </select>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      onClick={handleSaveInfo}
                      className="btn-primary"
                    >
                      <SaveIcon size={16} /> Sauvegarder
                    </button>
                    <button
                      onClick={() => setIsEditingInfo(false)}
                      className="btn-secondary"
                    >
                      <CancelIcon size={16} /> Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  {event.description && (
                    <div className="info-item">
                      <strong>Description :</strong>
                      <p>{event.description}</p>
                    </div>
                  )}
                  <div className="info-item">
                    <strong>Format :</strong>
                    <p>{event.is_online ? '🌐 En ligne' : '📍 Présentiel'}</p>
                  </div>
                  {!event.is_online && event.location && (
                    <div className="info-item">
                      <strong>Lieu :</strong>
                      <p>
                        {event.location.startsWith('http') ? (
                          <a href={event.location} target="_blank" rel="noopener noreferrer" style={{color: 'var(--primary)', textDecoration: 'underline'}}>
                            {event.location}
                          </a>
                        ) : event.location}
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Section Score pour scrims et tournois */}
            {(event.type?.toLowerCase() === 'scrim' || event.type?.toLowerCase() === 'tournament') && (
              <div className="event-score-card">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ margin: 0 }}>Match & Score</h3>
                  {!isEditingScore && (
                    <button
                      onClick={handleStartEditingScore}
                      className="btn-primary btn-sm"
                    >
                      <Edit2Icon size={14} /> Modifier
                    </button>
                  )}
                </div>
                {isEditingScore ? (
                  <div>
                    <div className="score-edit-form">
                      <div className="input-group">
                        <label className="input-label">Adversaire</label>
                        <input
                          type="text"
                          className="input"
                          value={scoreData.opponent}
                          onChange={(e) => setScoreData({ ...scoreData, opponent: e.target.value })}
                          placeholder="Nom de l'équipe adverse"
                        />
                      </div>
                      <div className="score-inputs-row">
                        <div className="input-group score-input-group">
                          <label className="input-label">Score TSK</label>
                          <input
                            type="number"
                            min="0"
                            className="input score-input"
                            value={scoreData.scoreTeam}
                            onChange={(e) => setScoreData({ ...scoreData, scoreTeam: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                        <span className="score-separator">-</span>
                        <div className="input-group score-input-group">
                          <label className="input-label">Score {scoreData.opponent || 'Adversaire'}</label>
                          <input
                            type="number"
                            min="0"
                            className="input score-input"
                            value={scoreData.scoreOpponent}
                            onChange={(e) => setScoreData({ ...scoreData, scoreOpponent: e.target.value })}
                            placeholder="0"
                          />
                        </div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                      <button
                        onClick={handleSaveScore}
                        className="btn-primary"
                      >
                        <SaveIcon size={16} /> Sauvegarder
                      </button>
                      <button
                        onClick={() => setIsEditingScore(false)}
                        className="btn-secondary"
                      >
                        <CancelIcon size={16} /> Annuler
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="score-display">
                    {event.opponent || event.score ? (
                      <>
                        {event.score && (
                          <div className="score-versus">
                            <div className="score-team">
                              <span className="score-team-name">TSK</span>
                              <span className="score-team-value">{parseScore(event.score).team}</span>
                            </div>
                            <span className="score-vs">-</span>
                            <div className="score-team">
                              <span className="score-team-name">{event.opponent || 'Adversaire'}</span>
                              <span className="score-team-value">{parseScore(event.score).opponent}</span>
                            </div>
                          </div>
                        )}
                        {!event.score && event.opponent && (
                          <div className="score-item">
                            <strong>Adversaire :</strong>
                            <span>{event.opponent}</span>
                          </div>
                        )}
                      </>
                    ) : (
                      <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                        Aucun adversaire ou score renseigné. Cliquez sur "Modifier" pour ajouter.
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}

            <div className="event-rich-notes-card">
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                <h3 style={{ margin: 0 }}>Notes</h3>
                {!isEditingNotes && (
                  <button
                    onClick={handleStartEditingNotes}
                    className="btn-primary btn-sm"
                  >
                    <Edit2Icon size={14} /> Modifier
                  </button>
                )}
              </div>
              {isEditingNotes ? (
                <div>
                  <ReactQuill
                    theme="snow"
                    value={richNotesContent}
                    onChange={setRichNotesContent}
                    modules={{
                      toolbar: [
                        [{ 'header': [1, 2, 3, false] }],
                        ['bold', 'italic', 'underline', 'strike'],
                        [{ 'list': 'ordered'}, { 'list': 'bullet' }],
                        [{ 'color': [] }, { 'background': [] }],
                        ['link', 'image'],
                        ['clean']
                      ]
                    }}
                  />
                  <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                    <button
                      onClick={handleSaveRichNotes}
                      className="btn-primary"
                    >
                      <SaveIcon size={16} /> Sauvegarder
                    </button>
                    <button
                      onClick={() => setIsEditingNotes(false)}
                      className="btn-secondary"
                    >
                      <CancelIcon size={16} /> Annuler
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rich-notes-content">
                  {event.rich_notes ? (
                    <div dangerouslySetInnerHTML={createSafeMarkup(event.rich_notes)} />
                  ) : event.notes ? (
                    <p>{event.notes}</p>
                  ) : (
                    <p style={{ color: 'var(--text-secondary)', fontStyle: 'italic' }}>
                      Aucune note. Cliquez sur "Modifier" pour en ajouter.
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* ONGLET PRÉSENCES */}
        {activeTab === 'attendances' && (
          <div className="event-attendances-tab">
            <div className="my-attendance-section">
              <h3>Ma présence</h3>
              <div className="attendance-buttons">
                <button
                  className={`attendance-btn present ${myAttendance?.status === 'present' ? 'active' : ''}`}
                  onClick={() => handleSetAttendance('present')}
                >
                  ✅ Présent
                </button>
                <button
                  className={`attendance-btn maybe ${myAttendance?.status === 'maybe' ? 'active' : ''}`}
                  onClick={() => handleSetAttendance('maybe')}
                >
                  ❓ Peut-être
                </button>
                <button
                  className={`attendance-btn absent ${myAttendance?.status === 'absent' ? 'active' : ''}`}
                  onClick={() => handleSetAttendance('absent')}
                >
                  ❌ Absent
                </button>
                {myAttendance && (
                  <button
                    className="attendance-btn remove"
                    onClick={handleRemoveAttendance}
                  >
                    Retirer ma réponse
                  </button>
                )}
              </div>
            </div>

            <div className="attendance-stats">
              <div className="stat-item present">
                <span className="stat-number">{stats.present}</span>
                <span className="stat-label">Présents</span>
              </div>
              <div className="stat-item maybe">
                <span className="stat-number">{stats.maybe}</span>
                <span className="stat-label">Incertains</span>
              </div>
              <div className="stat-item absent">
                <span className="stat-number">{stats.absent}</span>
                <span className="stat-label">Absents</span>
              </div>
            </div>

            <div className="attendances-list">
              <h3>Liste des participants</h3>
              {attendances.map(att => (
                <div key={att.id} className={`attendance-item ${att.status}`}>
                  <div className="attendance-user">
                    <span className="attendance-status-icon">
                      {att.status === 'present' && '✅'}
                      {att.status === 'absent' && '❌'}
                      {att.status === 'maybe' && '❓'}
                    </span>
                    <span className="username">{att.username}</span>
                  </div>
                  <span className="attendance-time">{formatRelativeTime(att.updated_at)}</span>
                </div>
              ))}
              {attendances.length === 0 && (
                <p className="empty-message">Aucune réponse pour le moment</p>
              )}
            </div>
          </div>
        )}

        {/* ONGLET COMMENTAIRES - Suite dans le prochain message */}
        {activeTab === 'comments' && (
          <div className="event-comments-tab">
            <form onSubmit={handleAddComment} className="comment-form">
              {replyingTo && (
                <div className="comment-reply-banner">
                  <span>En réponse à <strong>{replyingTo.username}</strong></span>
                  <button type="button" onClick={() => setReplyingTo(null)} className="btn-cancel-reply">
                    <CloseIcon size={14} />
                  </button>
                </div>
              )}
              <textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder={replyingTo ? `Répondre à ${replyingTo.username}...` : 'Ajouter un commentaire...'}
                rows="3"
              />
              <button type="submit" className="btn-primary" disabled={!newComment.trim()}>
                {replyingTo ? 'Répondre' : 'Publier'}
              </button>
            </form>

            <div className="comments-list">
              {comments.map(comment => (
                <div key={comment.id} className={`comment-item ${comment.parent_id ? 'comment-reply' : ''}`}>
                  <div className="comment-avatar">
                    {comment.user_avatar ? (
                      <img src={getStaticUrl(comment.user_avatar)} alt={comment.username} />
                    ) : (
                      <div className="comment-avatar-placeholder">
                        {comment.username?.charAt(0).toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="comment-body">
                    <div className="comment-header">
                      <span className="comment-author">{comment.username}</span>
                      {comment.reply_to_username && (
                        <span className="comment-reply-to">
                          ↩ {comment.reply_to_username}
                        </span>
                      )}
                      <span className="comment-time">{formatDateTime(comment.created_at)}</span>
                    </div>
                    <p className="comment-content">{comment.content}</p>
                    <button
                      className="btn-reply-comment"
                      onClick={() => setReplyingTo({ id: comment.id, username: comment.username })}
                    >
                      ↩ Répondre
                    </button>
                  </div>
                  {comment.user_id === user.id && (
                    <button
                      className="btn-delete-comment"
                      onClick={() => handleDeleteComment(comment.id)}
                    >
                      <TrashIcon size={14} />
                    </button>
                  )}
                </div>
              ))}
              {comments.length === 0 && (
                <p className="empty-message">Aucun commentaire pour le moment</p>
              )}
            </div>
          </div>
        )}

        {/* ONGLET PIÈCES JOINTES */}
        {activeTab === 'medias' && (
          <div className="event-medias-tab">
            <div className="media-upload-section">
              <h3>Ajouter une pièce jointe</h3>
              <div className="upload-buttons">
                <label className="btn-upload">
                  <input
                    type="file"
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.zip,.rar,.replay"
                    onChange={handleMediaUpload}
                    disabled={uploadingMedia}
                    style={{ display: 'none' }}
                  />
                  <FileIcon size={18} /> Importer un fichier
                </label>
              </div>
              {uploadingMedia && <p className="uploading-message">Upload en cours...</p>}
            </div>

            <div className="medias-grid">
              {medias.filter(m => m.media_type === 'image').map(media => (
                <div key={media.id} className="media-item image">
                  <img
                    src={getStaticUrl(media.file_path)}
                    alt={media.filename}
                    onClick={() => {
                      setSelectedImage(media);
                      setImageModalOpen(true);
                    }}
                    style={{ cursor: 'pointer' }}
                  />
                  <div className="media-info">
                    <span className="media-filename">{media.filename}</span>
                    <span className="media-author">Par {media.username}</span>
                  </div>
                  {media.user_id === user.id && (
                    <button
                      className="btn-delete-media"
                      onClick={() => handleDeleteMedia(media.id)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </div>
              ))}
              {medias.filter(m => m.media_type === 'video').map(media => (
                <div key={media.id} className="media-item video">
                  <video controls src={getStaticUrl(media.file_path)} />
                  <div className="media-info">
                    <span className="media-filename">{media.filename}</span>
                    <span className="media-author">Par {media.username}</span>
                  </div>
                  {media.user_id === user.id && (
                    <button
                      className="btn-delete-media"
                      onClick={() => handleDeleteMedia(media.id)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </div>
              ))}
              {medias.filter(m => m.media_type === 'file').map(media => (
                <div key={media.id} className="media-item file">
                  <div className="file-preview">
                    <FileIcon size={48} />
                  </div>
                  <div className="media-info">
                    <span className="media-filename">{media.filename}</span>
                    <span className="media-author">Par {media.username}</span>
                  </div>
                  <a
                    href={getStaticUrl(media.file_path)}
                    download={media.filename}
                    className="btn-download-media"
                    title="Télécharger"
                  >
                    <DownloadIcon size={16} />
                  </a>
                  {media.user_id === user.id && (
                    <button
                      className="btn-delete-media"
                      onClick={() => handleDeleteMedia(media.id)}
                    >
                      <TrashIcon size={16} />
                    </button>
                  )}
                </div>
              ))}
              {medias.length === 0 && (
                <p className="empty-message">Aucune pièce jointe pour le moment</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal pour afficher l'image en grand */}
      {imageModalOpen && selectedImage && (
        <div className="image-modal-overlay" onClick={() => setImageModalOpen(false)}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="image-modal-close" onClick={() => setImageModalOpen(false)}>
              ✕
            </button>
            <img src={getStaticUrl(selectedImage.file_path)} alt={selectedImage.filename} />
            <div className="image-modal-info">
              <p className="image-modal-filename">{selectedImage.filename}</p>
              <p className="image-modal-author">Par {selectedImage.username}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventDetail;
